import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * @typedef {HTMLElement & {
 *   pauseMedia?: () => void,
 *   loadContent?: (focus?: boolean) => void,
 *   playMedia?: () => void,
 *   deferAutoplay?: () => void
 * }} DeferredMediaElement
 */

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });
    if (this.dataset.syncAutoplayOnConnect === 'true') {
      delete this.dataset.syncAutoplayOnConnect;
      this.presentation === 'grid' ? this.#queueGridAutoplaySync() : this.#queueActiveSlideAutoplaySync();
    } else if (!this.#isDesktopViewport() && this.presentation !== 'grid') {
      this.#queueActiveSlideAutoplaySync();
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (event.detail.data.morphedMain) {
      // Combined-listing product switches morph main/section in place. Stable block ids can
      // leave the previous child's media reconciled incorrectly. Prefer an explicit gallery
      // replace when the response still has a media-gallery (section morph keeps it in `html`).
      if (event.detail.data.newProduct && source) {
        const newMediaGallery = source.querySelector('media-gallery');
        if (newMediaGallery && newMediaGallery !== this) {
          if (this.#isDesktopViewport()) {
            newMediaGallery.dataset.syncAutoplayOnConnect = 'true';
          }
          this.replaceWith(newMediaGallery);
          return;
        }
      }

      if (!this.#isDesktopViewport()) {
        this.#queueSelectedSlideAutoplaySync();
        return;
      }
      if (this.presentation === 'grid') {
        this.#queueGridAutoplaySync();
        return;
      }
      this.#queueActiveSlideAutoplaySync();
      return;
    }

    if (!source) return;

    // Stale same-product fetches can finish after a combined-listing main morph.
    // Their VariantUpdateEvent lacks morphedMain; ignore gallery HTML that belongs
    // to a different product than the live picker in this gallery's scope (section or
    // quick-add dialog — never fall back to another picker's product id on the page).
    const liveScope = this.closest('.shopify-section, dialog');
    const livePicker = liveScope?.querySelector('variant-picker');
    const liveProductId = livePicker instanceof HTMLElement ? livePicker.dataset.productId : null;
    const sourcePicker = source.querySelector('variant-picker');
    const sourceProductId = sourcePicker instanceof HTMLElement ? sourcePicker.dataset.productId : null;
    if (liveProductId && sourceProductId && liveProductId !== sourceProductId) return;

    const newMediaGallery = source.querySelector('media-gallery');
    if (!newMediaGallery) return;
    if (this.#isDesktopViewport()) {
      newMediaGallery.dataset.syncAutoplayOnConnect = 'true';
    }
    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Handles slideshow selection changes by autoplaying active video slides.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    if (this.presentation === 'grid' && this.#isDesktopViewport()) return;
    const selectedSlide = event.detail.slide;
    if (!(selectedSlide instanceof HTMLElement)) return;
    this.#syncAutoplayForSlide(selectedSlide, event.detail.index);
  };

  /**
   * @param {HTMLElement} selectedSlide
   * @param {number} selectedIndex
   */
  #syncAutoplayForSlide(selectedSlide, selectedIndex) {
    const autoplayMedia = /** @type {DeferredMediaElement[]} */ ([...this.querySelectorAll('deferred-media[autoplay]')]);
    if (!autoplayMedia.length) return;

    const selectedAutoplayMedia = /** @type {DeferredMediaElement | null} */ (
      selectedSlide.querySelector('deferred-media[autoplay]')
    );

    if (!(selectedAutoplayMedia instanceof HTMLElement)) {
      return;
    }

    for (const media of autoplayMedia) {
      if (media === selectedAutoplayMedia) continue;
      if (typeof media.pauseMedia === 'function') {
        media.pauseMedia();
      }
    }

    const hasInlineLoadedMedia = selectedAutoplayMedia.querySelector('video, iframe[data-video-type]') != null;
    const loadContent = selectedAutoplayMedia.loadContent;

    const needsDeferredLoad =
      typeof loadContent === 'function' &&
      !selectedAutoplayMedia.hasAttribute('data-media-loaded') &&
      !hasInlineLoadedMedia;

    if (needsDeferredLoad) {
      const { slideshow } = this;
      if (slideshow?.slides && typeof selectedIndex === 'number' && selectedIndex >= 0) {
        if (slideshow.current !== selectedIndex || slideshow.slides[selectedIndex] !== selectedSlide) return;
      } else if (selectedSlide.getAttribute('aria-hidden') !== 'false') {
        return;
      }

      if (selectedAutoplayMedia.hasAttribute('data-defer-autoplay')) {
        selectedAutoplayMedia.deferAutoplay?.();
        return;
      }

      loadContent.call(selectedAutoplayMedia, false);

      if (this.#isDesktopViewport()) {
        this.#ensureSelectedAutoplayPlayback(selectedAutoplayMedia, selectedSlide);
      } else if (selectedAutoplayMedia.querySelector('iframe[data-video-type]') != null) {
        selectedAutoplayMedia.playMedia?.();
      }
      return;
    }

    this.#ensureSelectedAutoplayPlayback(selectedAutoplayMedia, selectedSlide);
  }

  #queueActiveSlideAutoplaySync() {
    requestAnimationFrame(() => {
      this.#syncActiveSlideAutoplay();
    });

    window.setTimeout(() => {
      this.#syncActiveSlideAutoplay();
    }, 180);
  }

  #queueSelectedSlideAutoplaySync() {
    requestAnimationFrame(() => {
      this.#syncSelectedSlideAutoplay();
    });

    window.setTimeout(() => {
      this.#syncSelectedSlideAutoplay();
    }, 180);

    window.setTimeout(() => {
      this.#syncSelectedSlideAutoplay();
    }, 420);
  }

  #syncSelectedSlideAutoplay() {
    if (!this.isConnected) return;

    const activeSlide = this.#getActiveSlide();
    if (!(activeSlide instanceof HTMLElement)) return;
    if (!activeSlide.querySelector('deferred-media[autoplay]')) return;

    this.#syncAutoplayForSlide(activeSlide);
  }

  #syncActiveSlideAutoplay() {
    if (!this.isConnected) return;

    const activeSlide = this.#getActiveSlide();
    if (activeSlide instanceof HTMLElement && activeSlide.querySelector('deferred-media[autoplay]')) {
      this.#syncAutoplayForSlide(activeSlide, this.#getSlideIndex(activeSlide));
    }
  }

  #queueGridAutoplaySync() {
    requestAnimationFrame(() => {
      this.#syncGridAutoplay();
    });

    window.setTimeout(() => {
      this.#syncGridAutoplay();
    }, 180);
  }

  #syncGridAutoplay() {
    if (!this.isConnected) return;

    const grid = this.querySelector('.media-gallery__grid');
    if (!(grid instanceof HTMLElement)) return;

    const autoplayMedia = /** @type {DeferredMediaElement[]} */ ([...grid.querySelectorAll('deferred-media[autoplay]')]);
    if (!autoplayMedia.length) return;

    const selectedAutoplayMedia = autoplayMedia.find((media) => this.#isVisibleElement(media));
    if (!(selectedAutoplayMedia instanceof HTMLElement)) return;

    for (const media of autoplayMedia) {
      if (media === selectedAutoplayMedia) continue;
      media.pauseMedia?.();
    }

    const mediaContainer = selectedAutoplayMedia.closest('.product-media-container');
    if (!(mediaContainer instanceof HTMLElement)) return;

    const hasInlineLoadedMedia = selectedAutoplayMedia.querySelector('video, iframe[data-video-type]') != null;
    if (selectedAutoplayMedia.hasAttribute('data-defer-autoplay')) {
      selectedAutoplayMedia.deferAutoplay?.();
      return;
    }

    if (
      typeof selectedAutoplayMedia.loadContent === 'function' &&
      !selectedAutoplayMedia.hasAttribute('data-media-loaded') &&
      !hasInlineLoadedMedia
    ) {
      selectedAutoplayMedia.loadContent(false);
    }

    this.#ensureSelectedAutoplayPlayback(selectedAutoplayMedia, mediaContainer);
  }

  #getSlideIndex(slide) {
    const slides = this.slideshow?.refs?.slides;
    if (!Array.isArray(slides)) return -1;
    return slides.indexOf(slide);
  }

  #getActiveSlide() {
    const slideshowSlides = this.slideshow?.refs?.slides;
    if (Array.isArray(slideshowSlides) && slideshowSlides.length > 0) {
      const activeSlideshowSlide = slideshowSlides.find((slide) => slide.getAttribute('aria-hidden') === 'false');
      return activeSlideshowSlide ?? slideshowSlides[0] ?? null;
    }

    const activeDomSlide = this.querySelector('slideshow-slide[aria-hidden="false"]');
    if (activeDomSlide instanceof HTMLElement) return activeDomSlide;

    const firstDomSlide = this.querySelector('slideshow-slide');
    return firstDomSlide instanceof HTMLElement ? firstDomSlide : null;
  }

  #getFirstAutoplaySlide() {
    const slideshowSlides = this.slideshow?.refs?.slides;
    if (Array.isArray(slideshowSlides) && slideshowSlides.length > 0) {
      const autoplaySlide = slideshowSlides.find((slide) => slide.querySelector('deferred-media[autoplay]') != null);
      if (autoplaySlide) return autoplaySlide;
    }

    const autoplayDomSlide = this.querySelector('slideshow-slide:has(deferred-media[autoplay])');
    return autoplayDomSlide instanceof HTMLElement ? autoplayDomSlide : null;
  }

  #isDesktopViewport() {
    return window.matchMedia('(min-width: 750px)').matches;
  }

  #isVisibleElement(element) {
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return false;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    const visibleWidth = Math.max(0, Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0));
    const visibleHeight = Math.max(0, Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0));
    const visibleArea = visibleWidth * visibleHeight;
    const totalArea = rect.width * rect.height;
    if (!totalArea) return false;

    return visibleArea / totalArea >= 0.15;
  }

  #ensureSelectedAutoplayPlayback(media, slide) {
    if (media.hasAttribute('data-defer-autoplay')) {
      media.deferAutoplay?.();
      return;
    }
    if (typeof media.playMedia !== 'function') return;

    media.playMedia();

    if (this.#isDesktopViewport()) {
      requestAnimationFrame(() => {
        if (!this.isConnected || !slide.contains(media)) return;
        media.playMedia?.();
      });

      window.setTimeout(() => {
        if (!this.isConnected || !slide.contains(media)) return;
        media.playMedia?.();
      }, 180);
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const media = this.refs.zoomDialogComponent?.refs?.media;
    const zoomDialogMedia = media?.[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
